import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored session on mount
    const storedSession = localStorage.getItem('hyperlocal_session');
    const storedUser = localStorage.getItem('hyperlocal_user');
    
    if (storedSession && storedUser) {
      try {
        setSession(JSON.parse(storedSession));
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('hyperlocal_session');
        localStorage.removeItem('hyperlocal_user');
      }
    }
    setLoading(false);
  }, []);

  const signup = async (email, password, fullName) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, {
      email,
      password,
      name: fullName
    });
    
    if (response.data.session?.access_token) {
      setUser(response.data.user);
      setSession(response.data.session);
      localStorage.setItem('hyperlocal_session', JSON.stringify(response.data.session));
      localStorage.setItem('hyperlocal_user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password
    });
    
    if (response.data.session?.access_token) {
      setUser(response.data.user);
      setSession(response.data.session);
      localStorage.setItem('hyperlocal_session', JSON.stringify(response.data.session));
      localStorage.setItem('hyperlocal_user', JSON.stringify(response.data.user));
    }
    
    return response.data;
  };

  const logout = useCallback(async () => {
    try {
      if (session?.access_token) {
        await axios.post(`${API_URL}/api/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });
      }
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(null);
      setSession(null);
      localStorage.removeItem('hyperlocal_session');
      localStorage.removeItem('hyperlocal_user');
    }
  }, [session]);

  const getAuthHeader = useCallback(() => {
    if (session?.access_token) {
      return { Authorization: `Bearer ${session.access_token}` };
    }
    return {};
  }, [session]);

  const value = {
    user,
    session,
    loading,
    signup,
    login,
    logout,
    getAuthHeader,
    isAuthenticated: !!session?.access_token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
